"use client";

import { useState, useCallback, type ReactNode } from "react";
import { CalendarDays } from "lucide-react";
import { useMedia } from "react-use";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import { BookingDatePicker } from "@/components/product-detail/BookingDatePicker";
import { cn } from "@/lib/utils";
import { getDateFnsLocale } from "@/lib/utils/date-fns-locale";

interface DateRangePickerProps {
  checkinDate: string | null;
  checkoutDate: string | null;
  onDateChange: (checkin: string | null, checkout: string | null) => void;
  /** drawer 내부에 배치할 때 true */
  inDrawer?: boolean;
  /** 지도 위에 띄울 때 — 테두리 대신 링+그림자 */
  floating?: boolean;
  /** "chip" (기존 칩), "bar" (3버튼 바 스타일) */
  variant?: "chip" | "bar";
  /** bar 전용 — 데스크톱 가로 한 줄에서 글자 폭 알약으로(09-23). */
  inline?: boolean;
  /**
   * 기본 알약 대신 쓸 트리거 — 캠핑장 시트의 "날짜 변경" 글자 버튼처럼 다른 모양으로
   * 같은 달력을 열 때. 주면 variant 스타일은 무시된다.
   */
  trigger?: ReactNode;
}

function formatChipLabel(
  checkin: string | null,
  checkout: string | null,
  t: (key: string, params?: Record<string, string | number>) => string
): string {
  if (!checkin || !checkout) return t("dateFilter.selectDates");

  const ci = new Date(checkin + "T00:00:00");
  const co = new Date(checkout + "T00:00:00");
  const days =
    Math.round((co.getTime() - ci.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  const ciLabel = `${ci.getMonth() + 1}.${ci.getDate()}`;
  const coLabel = `${co.getMonth() + 1}.${co.getDate()}`;

  return `${ciLabel}~${coLabel} · ${t("dateFilter.days", { count: days })}`;
}

function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function DateRangePicker({
  checkinDate,
  checkoutDate,
  onDateChange,
  inDrawer = false,
  floating = false,
  variant = "chip",
  inline = false,
  trigger: customTrigger,
}: DateRangePickerProps) {
  const t = useTranslations("search");
  const locale = useLocale();
  const isMobile = useMedia("(max-width: 639px)", false);
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
  }, []);

  const hasDate = !!checkinDate && !!checkoutDate;
  const chipLabel = formatChipLabel(checkinDate, checkoutDate, t);

  const handleApply = useCallback(
    (pickupDate: Date, returnDate?: Date) => {
      if (returnDate) {
        onDateChange(toDateStr(pickupDate), toDateStr(returnDate));
      }
    },
    [onDateChange]
  );

  const trigger = customTrigger ? (
    customTrigger
  ) : variant === "bar" ? (
      <button
        type="button"
        className={cn(
          "flex h-[34px] items-center justify-center gap-1.5 rounded-full bg-white text-[13px] font-medium text-gray-700 transition-all duration-150 hover:bg-gray-50",
          // 한 줄 모드(SearchFilterBar inline)는 글자 폭 알약, 기본은 칸을 꽉 채운다
          inline ? "shrink-0 px-4" : "flex-2 w-full",
          // 데스크톱은 필터 알약과 같이 한 단계 크게(09-23)
          "mapShow:h-10 mapShow:text-sm",
          floating
            ? "pointer-events-auto ring-1 ring-black/10 shadow-[0_1px_2px_rgba(20,18,12,0.10),0_4px_12px_rgba(20,18,12,0.10)]"
            : "border border-gray-300"
        )}
      >
        <CalendarDays className="h-3.5 w-3.5" />
        <span className="whitespace-nowrap">{chipLabel}</span>
      </button>
    ) : (
      <button
        type="button"
        className={`flex items-center gap-1.5 px-3.5 py-2 ${
          inDrawer ? "" : "sm:gap-2 sm:px-4 sm:py-2.5"
        } rounded-full transition-all duration-150 shrink-0 border ${
          hasDate
            ? "bg-orange-50 text-orange-600 border-orange-200"
            : "bg-white text-gray-900 border-gray-200 hover:bg-gray-50"
        }`}
      >
        <CalendarDays
          className={`w-4 h-4 ${hasDate ? "text-orange-500" : "text-gray-700"}`}
        />
        <span
          className={`text-sm font-medium ${
            inDrawer ? "" : "sm:text-sm"
          } whitespace-nowrap`}
        >
          {chipLabel}
        </span>
      </button>
    );

  return (
    <>
      <div
        className={!customTrigger && variant === "bar" ? "flex-2" : undefined}
        onClick={() => setOpen(true)}
      >
        {trigger}
      </div>
      <BookingDatePicker
        open={open}
        onOpenChange={handleOpenChange}
        mode="daily"
        pickupDate={
          checkinDate ? new Date(checkinDate + "T00:00:00") : undefined
        }
        returnDate={
          checkoutDate ? new Date(checkoutDate + "T00:00:00") : undefined
        }
        onApply={handleApply}
        onReset={() => onDateChange(null, null)}
        locale={getDateFnsLocale(locale)}
        variant={isMobile ? "drawer" : "dialog"}
        standalone
        quickPicks
      />
    </>
  );
}
